<template>
  <v-app>
    <v-app-bar app>
      <v-toolbar-title>{{ t('admConfig.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
    </v-app-bar>
    
    <v-main>
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <v-icon left>mdi-cog</v-icon>
                {{ t('admConfig.configuration') }}
              </v-card-title>
              <v-card-text>
                <p>{{ t('admConfig.description') }}</p>
                
                <!-- Qui aggiungeremo i controlli di configurazione -->
                <v-row>
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-title>{{ t('admConfig.generalSettings') }}</v-card-title>
                      <v-card-text>
                        <!-- Root unica del contenitore: le sottocartelle sono fisse (config backend) -->
                        <v-text-field
                          :label="t('admConfig.rootPath')"
                          v-model="config.rootPath"
                          outlined
                          :placeholder="t('admConfig.rootPathPlaceholder')"
                          :hint="t('admConfig.rootPathHint')"
                          persistent-hint
                        />

                        <!-- Anteprima struttura risultante (sola lettura) -->
                        <v-sheet v-if="config.rootPath && folders.processes" color="grey-lighten-4" rounded class="pa-3 mt-4">
                          <div class="text-caption text-medium-emphasis mb-1">{{ t('admConfig.structurePreview') }}</div>
                          <pre class="text-body-2 mb-0" style="white-space: pre-wrap;">{{ structurePreview }}</pre>
                        </v-sheet>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                
                <v-row class="mt-4">
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-title>{{ t('admConfig.systemInfo') }}</v-card-title>
                      <v-card-text>
                        <v-simple-table>
                          <tbody>
                            <tr>
                              <td><strong>{{ t('admConfig.status') }}</strong></td>
                              <td>
                                <v-chip 
                                  :color="systemStatus.color" 
                                  small
                                >
                                  {{ t(`admConfig.${systemStatus.status}`) }}
                                </v-chip>
                              </td>
                            </tr>
                            <tr>
                              <td><strong>{{ t('admConfig.lastUpdate') }}</strong></td>
                              <td>{{ systemStatus.lastUpdate }}</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
              
              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  @click="saveConfiguration"
                  :loading="saving"
                  :disabled="!isConfigValid"
                >
                  {{ t('admConfig.save') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useLocalBackendStore } from '../stores/localBackend.js'
import { useProjectStore } from '../stores/projects.js'
import { API_CONFIG, apiCall } from '../utils/api.js'

const { t } = useI18n()
const router = useRouter()
const localBackendStore = useLocalBackendStore()
const projectStore = useProjectStore()

// Stato reattivo per la configurazione (solo la root del contenitore)
const config = reactive({
  rootPath: ''
})

// Stato del sistema
const systemStatus = reactive({
  status: 'online',
  color: 'success',
  lastUpdate: new Date().toLocaleString()
})

const saving = ref(false)

// Config strutturale cartelle (dal Backend) per l'anteprima
const folders = computed(() => projectStore.folders || {})

const isConfigValid = computed(() => true)

// Anteprima della struttura risultante dalla root + nomi fissi
const structurePreview = computed(() => {
  const root = (config.rootPath || '').replace(/[\\/]+$/, '')
  const f = folders.value
  if (!root || !f.processes) return ''
  const byType = f.templatesByType || {}
  const templateLines = Object.values(byType).map(name => `    ${name}/`)
  const note = t('admConfig.allTypesNote')
  return [
    `${root}\\`,
    `  ${f.processes}/   (${note})`,
    `  ${f.templates}/`,
    ...templateLines,
    `  ${f.archive}/   (${note})`
  ].join('\n')
})

// Metodi
const goBack = () => {
  router.push('/home')
}

const saveConfiguration = async () => {
  saving.value = true
  try {
    // Salva la root nel LocalBackend
    const result = await apiCall('/api/config/config', {
      method: 'PUT',
      body: JSON.stringify({
        rootPath: config.rootPath
      }),
      backend: 'local',
      includeAuth: false
    })

    console.log('Configurazione salvata con successo', result)

    // Riverifica la configurazione dopo il salvataggio
    await localBackendStore.checkConnection()

    // Torna alla dashboard dopo il salvataggio
    router.push('/home')
  } catch (error) {
    console.error('Errore nel salvataggio:', error)
  } finally {
    saving.value = false
  }
}

const loadConfiguration = async () => {
  try {
    // Carica la root dal LocalBackend
    const pathsConfig = await apiCall('/api/config/config', {
      method: 'GET',
      backend: 'local',
      includeAuth: false
    })

    if (pathsConfig) {
      config.rootPath = pathsConfig.rootPath || ''
    }
  } catch (error) {
    console.error('Errore nel caricamento:', error)
  }
}

onMounted(async () => {
  // Assicura che la config strutturale (nomi cartelle) sia disponibile per l'anteprima
  if (!folders.value.processes) {
    await projectStore.loadConfigurations()
  }
  loadConfiguration()
})
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}
</style>